import ErrorIcon from "@mui/icons-material/Error";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  AlertTitle,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import isEqual from "lodash.isequal";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  fetchLatestBidAllotmentEventsByReferenceNumber,
  fetchLatestBidGroupEmpEventsByReferenceNumber,
} from "../../api/bid.js";
import { fetchAllotmentValueByStationWorkgroupAndYear, fetchEmployee } from "../../api/employee.js";
import { getPresignedUrl, uploadFileToS3 } from "../../api/system";
import PageLoading from "../../components/PageLoading.jsx";
import BidScheduledStatusChip from "../../components/bid/BidScheduledStatusChip.jsx";
import BidTimer from "../../components/bid/BidTimer.jsx";
import CreateIAMDATBidGroup from "../../components/bid/CreateIAMDATBidGroup.jsx";
import IAMDATBidAward from "../../components/bid/IAMDATBidAward.jsx";
import ManageIAMDATAllotments from "../../components/bid/ManageIAMDATAllotments.jsx";
import ManageIAMDATBidChoices from "../../components/bid/ManageIAMDATBidChoices.jsx";
import SetUpIAMDATBid from "../../components/bid/SetUpIAMDATBid.jsx";
import ConfirmButton from "../../components/fundamentals/ConfirmButton";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog.jsx";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../../components/fundamentals/SuccessDialog";
import { selectStationFilter, selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { generateIAMDATDayStateRows } from "../../util/bid/IAMDATBidUtils.js";
import { validateAllotments } from "../../util/bid/IAMDATBidValidations";
import { clearMTOBidAwardData } from "../../util/bid/bidAward.js";
import {
  getCurrentFormScheduledStatus,
  getS3ObjectName,
  isAwardedBid,
  isClosedBid,
  isDeletedBid,
  isOpenBid,
  isPendingBid,
  longPollingBidFetch,
} from "../../util/bid/bidCommon.js";
import { BID_SCHEDULED_STATUS, BID_STATUS, IAM_DAT_BID_AWARD_TYPE } from "../../util/bid/bidConstants";
import { buildAllotmentsPayload, buildTimeOffBidS3Data } from "../../util/bid/bidEventBuilder.js";
import { getEligibleEmployees, getEmployeeUnrankedCount } from "../../util/bid/bidGroupUtils.js";
import {
  validateBidEffectiveDates,
  validateBidName,
  validateBidWindowTime,
  validateIAMDATBidWindowDates,
} from "../../util/bid/bidInputValidation.js";
import { calcDailyAllotmentInitialState, getTotalAllotments } from "../../util/bid/timeOffBidUtils.js";
import { getDayStateForIAMDATAllotment, getFormattedIAMDATAllotmentDayState } from "../../util/calendar.js";
import { copyJsonObject } from "../../util/commonUtils.js";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  CONFIRM_TITLE,
  FEATURES,
  HOME_PATH,
  MANAGE_TIMEOFF_BID_TABS,
  SELECT_IAM_DAT_BID_EFFECTIVE_MONTH_LIST,
} from "../../util/constants.js";
import { padTime } from "../../util/dateTimeFormatter.js";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  REFERENCE_NUMBER_SUB_TITLE,
} from "../../util/messageConstants";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper.js";

const ManageIAMDATBidPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const [isBidGroupLoading, setIsBidGroupLoading] = useState(false);
  const [isBidAllotmentsLoading, setIsBidAllotmentsLoading] = useState(false);
  const stationFilter = useSelector(selectStationFilter);

  const [schedMonth, setSchedMonth] = useState("");
  const [schedYear, setSchedYear] = useState("");
  const [effectiveDatesErrors, setEffectiveDatesErrors] = useState("");
  const [windowDates, setWindowDates] = useState([null, null]);
  const [windowDatesErrors, setWindowDatesErrors] = useState("");
  const [windowDateHr, setWindowDateHr] = useState("");
  const [windowDateMin, setWindowDateMin] = useState("");
  const [windowDateTimeErrors, setWindowDateTimeErrors] = useState("");
  const [bidName, setBidName] = useState("");
  const [bidNameErrors, setBidNameErrors] = useState("");
  const [bidGroup, setBidGroup] = useState([]);
  const [initialBidGroup, setInitialBidGroup] = useState([]);
  const [isBidGroupManuallyLoaded, setIsBidGroupManuallyLoaded] = useState(false);
  const [calcDailyAllotments, setCalcDailyAllotments] = useState(copyJsonObject(calcDailyAllotmentInitialState));
  const [allotmentDayState, setAllotmentDayState] = useState([]);
  const [initialAllotmentDayState, setInitialAllotmentDayState] = useState([]);
  const [isSchedMonthOrYearChanged, setIsSchedMonthOrYearChanged] = useState(false);
  const [isBidGroupTabChanged, setIsBidGroupTabChanged] = useState(false);
  const [isBidAllotmentsTabChanged, setIsBidAllotmentsTabChanged] = useState(false);

  // Dialog state
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [saveErrors, setSaveErrors] = useState("");
  const [isReadyToBeScheduledByUser, setIsReadyToBeScheduledByUser] = useState(false);

  const isInitialPageLoadRef = useRef(true);
  const initialBidState = useRef(null);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isManageIAMDATBidChoicesSelected, setIsManageIAMDATBidChoicesSelected] = useState(false);
  const [selectedBidGroupEmployeeData, setSelectedBidGroupEmployeeData] = useState("");
  const [selectedAwardedBid, setSelectedAwardedBid] = useState(null);
  const [isAwardedNameSelected, setIsAwardedNameSelected] = useState(false);

  useEffect(() => {
    if (props.isDesktopView) {
      loadBidData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDesktopView]);

  useEffect(() => {
    setWindowDatesErrors(validateIAMDATBidWindowDates(windowDates, schedMonth, schedYear, props.bidData.status));
    // eslint-disable-next-line
  }, [windowDates, schedMonth, schedYear]);

  // set bid metadata fields into state
  useEffect(() => {
    if (props.bidData?.windowStartDate) {
      setWindowDates([props.bidData.windowStartDate, props.bidData.windowEndDate]);
      setWindowDateHr(padTime(props.bidData.windowStartDate.hour));
      setWindowDateMin(padTime(props.bidData.windowStartDate.minute));
    }

    if (props.bidData?.schedMonth) {
      setSchedMonth(props.bidData?.schedMonth);
    }
    if (props.bidData?.schedYear) {
      setSchedYear(props.bidData.schedYear);
    }

    if (props.bidData?.bidName) {
      setBidName(props.bidData.bidName);
    }

    setIsPageLoading(false);
  }, [props.bidData]);

  // useEffect for detecting change in schedMonth or schedYear
  useEffect(() => {
    if (allotmentDayState.length > 0 && schedMonth !== "" && schedYear !== "" && bidGroup.length > 0) {
      setIsSchedMonthOrYearChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedMonth, schedYear]);

  // useEffect for calculating allotments whenever a change in schedMonth or schedYear or bidGroup
  useEffect(() => {
    if (schedMonth !== "" && schedYear !== "" && bidGroup.length > 0 && isBidGroupManuallyLoaded) {
      calculateAllotments();
      setIsBidGroupManuallyLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedMonth, schedYear, bidGroup]);

  // useEffect for detecting isInitialPageLoad
  useEffect(() => {
    if (!isPageLoading && !isBidAllotmentsLoading && !isBidGroupLoading && isInitialPageLoadRef.current) {
      initialBidState.current = getKeyDiffData();
      isInitialPageLoadRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageLoading, isBidAllotmentsLoading, isBidGroupLoading]);

  // useEffect for detecting changes in state
  useEffect(() => {
    if (!isInitialPageLoadRef.current) {
      setIsFormChanged(true);
    }
  }, [isFormChanged, windowDates, windowDateHr, windowDateMin, schedMonth, schedYear, allotmentDayState, bidGroup]);

  const getBidAdminPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.BID_ADMINISTRATION).path;
  };

  const getReferenceNumberFromHref = () => {
    const uriList = window.location.href.split("/");
    return uriList[uriList.length - 1];
  };

  const handleClickCancelDialogOpen = () => {
    setIsExitDialogOpen(true);
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const isSetUpIAMDATBidTabInputValid = () => {
    return (
      effectiveDatesErrors === "" && windowDatesErrors === "" && windowDateTimeErrors === "" && bidNameErrors === ""
    );
  };

  const handleEffectiveMonthChange = (event) => {
    setEffectiveDatesErrors(validateBidEffectiveDates(event.target.value, schedYear));
    setSchedMonth(event.target.value);
  };

  const handleEffectiveYearChange = (event) => {
    setEffectiveDatesErrors(validateBidEffectiveDates(schedMonth, event.target.value));
    setSchedYear(event.target.value);
    setSchedMonth("");
  };

  const handleWindowDateChange = (event) => {
    setWindowDatesErrors(validateIAMDATBidWindowDates(event, schedMonth, schedYear, props.bidData.status));
    if (event[0] && !event[1]) {
      event[1] = event[0].plus({ days: 7 });
    }
    let newWindowStartDate = event[0];
    let newWindowEndDate = event[1];
    if (windowDates[0] && windowDates[1] && windowDateHr && windowDateMin) {
      newWindowStartDate = newWindowStartDate?.set({ hour: windowDateHr, minute: windowDateMin });
      newWindowEndDate = newWindowEndDate?.set({ hour: windowDateHr, minute: windowDateMin });
    }
    setWindowDates([newWindowStartDate, newWindowEndDate]);
  };

  const handleBidWindowHourChange = (event) => {
    setWindowDateTimeErrors(
      validateBidWindowTime(windowDates, event.target.value, windowDateMin, stationFilter, props.bidData.status)
    );
    let newWindowStartDate = windowDates[0];
    let newWindowEndDate = windowDates[1];
    newWindowStartDate = newWindowStartDate.set({ hour: parseInt(event.target.value) });
    newWindowEndDate = newWindowEndDate.set({ hour: parseInt(event.target.value) });
    setWindowDates([newWindowStartDate, newWindowEndDate]);
    setWindowDateHr(event.target.value);
  };

  const handleBidWindowMinuteChange = (event) => {
    setWindowDateTimeErrors(
      validateBidWindowTime(windowDates, windowDateHr, event.target.value, stationFilter, props.bidData.status)
    );
    let newWindowStartDate = windowDates[0];
    let newWindowEndDate = windowDates[1];
    newWindowStartDate = newWindowStartDate.set({ minute: parseInt(event.target.value) });
    newWindowEndDate = newWindowEndDate.set({ minute: parseInt(event.target.value) });
    setWindowDates([newWindowStartDate, newWindowEndDate]);
    setWindowDateMin(event.target.value);
  };

  const handleBidNameChange = (event) => {
    setBidNameErrors(validateBidName(event.target.value));
    setBidName(event.target.value);
  };

  const isAllotmentTabValid = () => {
    if (isBidAllotmentsLoading || isBidGroupLoading) {
      return true;
    }
    return !isSchedMonthOrYearChanged;
  };

  const isAwardTabAvailable = () => {
    return props.bidData.status === BID_STATUS.CLOSED || props.bidData.status === BID_STATUS.AWARDED;
  };

  const handleAwardedNameClick = (awardedBid) => {
    setIsAwardedNameSelected(true);
    fetchEmployee(awardedBid.eid)
      .then((res) => res.json())
      .then((response) => {
        if (response) {
          setSelectedBidGroupEmployeeData(response);
          setSelectedAwardedBid(awardedBid);
        } else {
          console.error("ERROR");
        }
      });
  };

  const calculateAllotments = () => {
    setIsBidAllotmentsLoading(true);

    fetchAllotmentValueByStationWorkgroupAndYear(props.bidData.station, props.bidData.workgroup, schedYear)
      .then((res) => res.json())
      .then((response) => {
        if (response.pk) {
          const calcDailyAllotments = {
            amOrPmCount: response?.allotmentValue,
          };
          setCalcDailyAllotments(calcDailyAllotments);
          setAllotmentDayState(getDayStateForIAMDATAllotment(schedYear, schedMonth, calcDailyAllotments));
        } else {
          // when allotmentValue is not returned from the api default to 1. (this will useful for 2025 or any year when calc is not done yet)
          const calcDailyAllotments = {
            amOrPmCount: 1,
          };
          setCalcDailyAllotments(calcDailyAllotments);
          setAllotmentDayState(getDayStateForIAMDATAllotment(schedYear, schedMonth, calcDailyAllotments));
        }
        setIsBidAllotmentsLoading(false);
        setIsSchedMonthOrYearChanged(false);
      })
      .catch((err) => {
        console.error("error", err);
        setCalcDailyAllotments(calcDailyAllotmentInitialState);
        setAllotmentDayState([]);

        setIsBidAllotmentsLoading(false);
      });
  };

  const handleEmployeeChoicesAsAdmin = (employeeData) => {
    setSelectedBidGroupEmployeeData(employeeData);
    setIsManageIAMDATBidChoicesSelected(true);
  };

  const handleBidGroupChange = (event) => {
    setBidGroup(event);
    setIsBidGroupTabChanged(true);
  };

  const loadBidData = () => {
    isInitialPageLoadRef.current = true;
    setIsFormChanged(false);
    setIsBidGroupLoading(true);
    setIsBidAllotmentsLoading(true);
    const referenceNumber = getReferenceNumberFromHref();
    fetchLatestBidGroupEmpEventsByReferenceNumber(userSession.eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          setBidGroup(response.events);
          setInitialBidGroup(response.events);
          setIsBidGroupLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Group Error: %j`, err);
        props.setLoadErrors(true);
        setBidGroup([]);
        setInitialBidGroup([]);
        setIsBidGroupLoading(false);
      });

    fetchLatestBidAllotmentEventsByReferenceNumber(userSession.eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          if (response.events.allotments && response.events.calcDailyAllotments) {
            const reconstructedAllotments = getFormattedIAMDATAllotmentDayState(
              response.events.allotments,
              props.bidData.schedMonth,
              props.bidData.schedYear
            );
            setCalcDailyAllotments(response.events.calcDailyAllotments);
            setAllotmentDayState(reconstructedAllotments);
            setInitialAllotmentDayState(reconstructedAllotments);
          }
          setIsBidAllotmentsLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Allotment Error: %j`, err);
        props.setLoadErrors(true);
        setAllotmentDayState([]);
        setInitialAllotmentDayState([]);
        setCalcDailyAllotments(copyJsonObject(calcDailyAllotmentInitialState));
        setIsBidAllotmentsLoading(false);
      });
  };

  const handleAllotmentDayStateChange = (event) => {
    setAllotmentDayState(event);
    setIsBidAllotmentsTabChanged(true);
  };

  const handleClickSubmitDialogOpen = () => {
    if (!isFormValidationSuccess()) {
      setIsSaveDialogOpen(false);
      return;
    }
    setIsReadyToBeScheduledByUser(false);
    setIsSaveDialogOpen(true);
  };

  const handleClickSaveDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSaveDialogOpen(false);
  };

  const isFormValidationSuccess = () => {
    const localBidEffectiveDatesError = validateBidEffectiveDates(schedMonth, schedYear);
    setEffectiveDatesErrors(localBidEffectiveDatesError);
    const localWindowDateErrors = validateIAMDATBidWindowDates(
      windowDates,
      schedMonth,
      schedYear,
      props.bidData.status
    );
    setWindowDatesErrors(localWindowDateErrors);

    const localWindowDateTimeErrors = validateBidWindowTime(
      windowDates,
      windowDateHr,
      windowDateMin,
      stationFilter,
      props.bidData.status
    );
    setWindowDateTimeErrors(localWindowDateTimeErrors);

    const localBidNameErrors = validateBidName(bidName);
    setBidNameErrors(localBidNameErrors);

    return (
      localBidEffectiveDatesError === "" &&
      localWindowDateErrors === "" &&
      localWindowDateTimeErrors === "" &&
      localBidNameErrors === ""
    );
  };

  const getSchedulableValidationErrors = () => {
    let validationErrors = [];

    if (!schedMonth || !schedYear) {
      validationErrors.push("Bid Effective Month and Year are missing");
    }

    if (!windowDates[0] || !windowDates[1] || !windowDateHr || !windowDateMin) {
      validationErrors.push("Bid Window Dates are missing");
    }

    const unrankedEmployeeCount = getEmployeeUnrankedCount(getEligibleEmployees(bidGroup));
    if (unrankedEmployeeCount > 0) {
      if (unrankedEmployeeCount === 1) {
        validationErrors.push(`${unrankedEmployeeCount} Employee is unranked`);
      } else {
        validationErrors.push(`${unrankedEmployeeCount} Employees are unranked`);
      }
    }
    if (bidGroup.length === 0 && getTotalAllotments(allotmentDayState) === 0) {
      validationErrors.push("Allotments and Eligible Employees are missing");
    }
    return validationErrors;
  };

  const getHardStopValidationErrors = () => {
    let validationErrors = [];
    if (isSchedMonthOrYearChanged) {
      validationErrors.push(
        "The Bid Effective Month/Year has changed. Please click Recalculate to update the allotments."
      );
    }

    if (isOpenBid(props.bidData.status) || isClosedBid(props.bidData.status)) {
      // unranked employees found
      const unrankedEmployeeCount = getEmployeeUnrankedCount(getEligibleEmployees(bidGroup));
      if (unrankedEmployeeCount > 0) {
        if (unrankedEmployeeCount === 1) {
          validationErrors.push(`${unrankedEmployeeCount} Employee is unranked`);
        } else {
          validationErrors.push(`${unrankedEmployeeCount} Employees are unranked`);
        }
      }
    }
    //Allotment mismatch validations are Hard validations in bid closed status
    if (isClosedBid(props.bidData.status)) {
      // Incorrect allotments
      const bidAllotmentErrors = validateAllotments(
        bidGroup,
        getTotalAllotments(allotmentDayState),
        calcDailyAllotments,
        schedMonth,
        schedYear
      );
      if (bidAllotmentErrors) {
        validationErrors.push(bidAllotmentErrors);
      }
    }
    return validationErrors;
  };

  const getSoftValidationErrors = () => {
    let validationErrors = [];

    //Incorrect allotments
    const bidAllotmentErrors = validateAllotments(
      bidGroup,
      getTotalAllotments(allotmentDayState),
      calcDailyAllotments,
      schedMonth,
      schedYear
    );
    if (bidAllotmentErrors) {
      validationErrors.push(bidAllotmentErrors);
    }
    return validationErrors;
  };

  const renderScheduledSection = () => {
    const schedulableValidationErrors = getSchedulableValidationErrors();
    const hardStopValidationErrors = getHardStopValidationErrors();
    return (
      <>
        {hardStopValidationErrors.length > 0 && (
          <>
            <Typography sx={{ marginTop: "30px" }}>Please fix the following before the bid can be saved:</Typography>
            {hardStopValidationErrors.map((validationError) => {
              return (
                <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
                  <ErrorIcon sx={{ marginRight: "5px", color: "primary.swaRed" }} />
                  {validationError}
                </Typography>
              );
            })}
          </>
        )}
        {schedulableValidationErrors.length > 0 && (
          <>
            <Typography sx={{ marginTop: "30px" }}>
              Please fix the following before the bid can be scheduled:
            </Typography>
            {schedulableValidationErrors.map((validationError) => {
              return (
                <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
                  <WarningIcon sx={{ marginRight: "5px", color: "primary.swaYellow" }} />
                  {validationError}
                </Typography>
              );
            })}
          </>
        )}
        <FormControlLabel
          label="Bid is ready to be scheduled"
          control={
            <Checkbox
              checked={isReadyToBeScheduledByUser}
              onChange={() => setIsReadyToBeScheduledByUser(!isReadyToBeScheduledByUser)}
              disabled={schedulableValidationErrors.length > 0 || hardStopValidationErrors.length > 0 || isSaveLoading}
            />
          }
          sx={{ marginTop: "10px", marginLeft: "1px" }}
        ></FormControlLabel>
      </>
    );
  };

  const renderHardStopErrorSection = () => {
    const validationErrors = getHardStopValidationErrors();
    if (validationErrors.length > 0) {
      return validationErrors.map((validationError) => {
        return (
          <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
            <ErrorIcon sx={{ marginRight: "5px", color: "primary.swaRed" }} />
            {validationError}
          </Typography>
        );
      });
    }
  };

  const renderSoftValidationErrorSection = () => {
    const validationErrors = getSoftValidationErrors();
    if (validationErrors.length > 0) {
      return validationErrors.map((validationError) => {
        return (
          <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
            <WarningIcon sx={{ marginRight: "5px", color: "primary.swaYellow" }} />
            {validationError}
          </Typography>
        );
      });
    }
  };

  const getUnAwardedList = (bidGroup) => {
    return bidGroup
      .filter(
        (emp) =>
          emp.awardType === IAM_DAT_BID_AWARD_TYPE.NO_BID ||
          emp.awardType === IAM_DAT_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES ||
          emp.awardType === IAM_DAT_BID_AWARD_TYPE.NO_SCHEDULE ||
          emp.awardType === IAM_DAT_BID_AWARD_TYPE.ZERO_BALANCE
      )
      .map((emp, idx) => ({ ...emp, id: idx }));
  };

  const getKeyDiffData = () => {
    let keyDiffData = {
      bidMetadata: {
        schedMonth: schedMonth,
        schedYear: schedYear,
        windowDates: windowDates,
        windowDateHr: windowDateHr,
        windowDateMin: windowDateMin,
        bidName: bidName,
      },
      bidAllotments: buildAllotmentsPayload(allotmentDayState, calcDailyAllotments),
      bidGroup,
    };
    keyDiffData = copyJsonObject(keyDiffData);
    return keyDiffData;
  };

  const isBidDataChanged = () => {
    if (!isInitialPageLoadRef.current) {
      const isKeyDataChanged = !isEqual(initialBidState.current, getKeyDiffData());
      let isScheduledStatusChanged = false;
      if (isPendingBid(props.bidData.status)) {
        if (props.bidData.scheduledStatus !== getCurrentFormScheduledStatus(isReadyToBeScheduledByUser))
          isScheduledStatusChanged = true;
      }
      return isKeyDataChanged || isScheduledStatusChanged;
    }
  };

  const onSaveSuccess = () => {
    setIsSaveDialogOpen(false);
    setIsSaveLoading(false);
    setIsBidAllotmentsTabChanged(false);
    setIsBidGroupTabChanged(false);
    setIsSuccessDialogOpen(true);
  };

  const onSaveError = () => {
    setSaveErrors("Unexpected Error");
    setIsSaveDialogOpen(false);
    setIsSaveLoading(false);
    setIsSuccessDialogOpen(true);
  };

  const isBidWindowLessThanSevenDays = () => {
    return windowDates[0] && windowDates[1] && windowDates[1].diff(windowDates[0], ["days"]).values.days < 7;
  };

  const renderSaveIAMDATBidDialog = () => {
    return (
      <Dialog open={isSaveDialogOpen} TransitionComponent={SlideUpTransition} keepMounted fullWidth>
        <DialogTitle>Save changes</DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"}>
            <Grid container>
              <Grid item xs={12}>
                <Typography sx={{ color: "black", marginBottom: "10px" }}>
                  You are saving changes made to this IAM DAT Bid.
                </Typography>
              </Grid>
            </Grid>
          </DialogContentText>
          {(isPendingBid(props.bidData.status) || isOpenBid(props.bidData.status)) &&
            renderSoftValidationErrorSection()}
          {isPendingBid(props.bidData.status) && (
            <>
              {isBidWindowLessThanSevenDays() ? (
                <Alert severity="warning" sx={{ marginBottom: "30px" }}>
                  <AlertTitle>
                    <strong>WARNING</strong>
                  </AlertTitle>
                  You have entered a Bid Window with a duration less than 7 days.
                </Alert>
              ) : (
                ""
              )}
              {renderScheduledSection()}
            </>
          )}
          {(isOpenBid(props.bidData.status) || isClosedBid(props.bidData.status)) && renderHardStopErrorSection()}
        </DialogContent>
        <DialogActions>
          <ConfirmButton
            variant="outlined"
            color="primary"
            onClick={handleClickSaveDialogClose}
            disabled={isSaveLoading}
          >
            Cancel
          </ConfirmButton>
          <ConfirmButton
            variant="contained"
            color="primary"
            onClick={handleSaveClicked}
            isLoading={isSaveLoading}
            disabled={
              (isPendingBid(props.bidData.status) ||
                isOpenBid(props.bidData.status) ||
                isClosedBid(props.bidData.status)) &&
              getHardStopValidationErrors().length > 0
            }
          >
            Save
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const getSchedStartDateFromMonth = (schedMonth, schedYear) => {
    const converted_month = SELECT_IAM_DAT_BID_EFFECTIVE_MONTH_LIST.indexOf(schedMonth) + 1;
    const converted_year = parseInt(schedYear);
    const date = DateTime.local(converted_year, converted_month).startOf("month");
    return date;
  };

  const getSchedEndDateFromMonth = (schedMonth, schedYear) => {
    const converted_month = SELECT_IAM_DAT_BID_EFFECTIVE_MONTH_LIST.indexOf(schedMonth) + 1;
    const converted_year = parseInt(schedYear);
    const date = DateTime.local(converted_year, converted_month).endOf("month");
    return date;
  };

  const prepareNewBidEventPayload = () => {
    const newBidEvent = {
      ...(props.bidData.awardLog ? { awardLog: props.bidData.awardLog } : {}),
      bidName: bidName,
      bidType: props.bidData.bidType,
      referenceNumber: props.bidData.referenceNumber,
      schedMonth: schedMonth,
      schedYear: schedYear,
      station: props.bidData.station,
      ...(props.bidData.stationTier ? { stationTier: props.bidData.stationTier } : {}),
      status: props.bidData.status,
      windowStartDate: windowDates[0],
      windowEndDate: windowDates[1],
      workgroup: props.bidData.workgroup,
      unionContract: getUnionContractGivenWorkgroup(props.bidData.workgroup),
      v: props.bidData.v,
      schedStartDate: getSchedStartDateFromMonth(schedMonth, schedYear),
      schedEndDate: getSchedEndDateFromMonth(schedMonth, schedYear),
    };

    if (isPendingBid(props.bidData.status)) {
      newBidEvent.scheduledStatus = getCurrentFormScheduledStatus(isReadyToBeScheduledByUser);
    }
    return newBidEvent;
  };

  const handleSaveClicked = async () => {
    if (!isFormValidationSuccess()) {
      setIsSaveDialogOpen(false);
      return;
    }
    setIsSaveLoading(true);
    let isAwardDataCleared = false;
    let currentAllotmentDayState = allotmentDayState;
    let currentBidGroup = bidGroup;
    if (isBidAllotmentsTabChanged || isBidGroupTabChanged) {
      const clearedDataResult = clearMTOBidAwardData(allotmentDayState, bidGroup);
      currentAllotmentDayState = clearedDataResult.allotmentDayState;
      currentBidGroup = clearedDataResult.bidGroup;
      setAllotmentDayState(clearedDataResult.allotmentDayState);
      setBidGroup(clearedDataResult.bidGroup);
      isAwardDataCleared = true;
    }

    // Save Bid Data to S3
    try {
      if (!isBidDataChanged() && !isAwardDataCleared) {
        // If no changes just show success message and exit
        onSaveSuccess();
        return;
      }

      const s3ObjectName = await getS3ObjectName(props.bidData.pk, props.bidData.station);
      const allotmentsPayload = buildAllotmentsPayload(currentAllotmentDayState, calcDailyAllotments);
      const res = await getPresignedUrl("put_object", "bid-data", s3ObjectName);
      const result = await res.json();
      if (result.presignedUrl) {
        const bidS3Data = buildTimeOffBidS3Data(
          userSession,
          prepareNewBidEventPayload(),
          allotmentsPayload,
          currentBidGroup,
          s3ObjectName
        );
        const response = await uploadFileToS3(result.presignedUrl, bidS3Data);

        if (response.status === 200) {
          const requestId = response.headers.get("x-amz-request-id");
          props.currentRequestId.current = requestId;
          await longPollingBidFetch(props.bidData.referenceNumber, userSession, requestId);
          onSaveSuccess();
        } else {
          const errorMessage = "Bad response from uploadFileToS3";
          console.error(errorMessage);
          throw Error(errorMessage);
        }
      } else {
        const errorMessage = "Bad response from getPresignedUrl";
        console.error(errorMessage);
        throw Error(errorMessage);
      }
    } catch (err) {
      console.error("Error during handleSaveClicked: ", err);
      onSaveError();
    }
  };

  const handleManageIAMDATBidBackClick = () => {
    setSelectedBidGroupEmployeeData("");
    setSelectedAwardedBid(null);
    setIsAwardedNameSelected(false);
  };

  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.BID_ADMINISTRATION,
    BREADCRUMBS_TITLE.MANAGE_IAM_DAT_BID,
  ];
  const breadcrumbsLinks = [HOME_PATH, getBidAdminPath()];
  const renderDesktopManageIAMDATBidPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {isManageIAMDATBidChoicesSelected ? (
          <ManageIAMDATBidChoices
            selectedBidGroupEmployeeData={selectedBidGroupEmployeeData}
            referenceNumber={getReferenceNumberFromHref()}
            featureConfig={props.featureConfig}
            setIsManageIAMDATBidChoicesSelected={setIsManageIAMDATBidChoicesSelected}
            handleManageIAMDATBidBackClick={handleManageIAMDATBidBackClick}
            station={props.bidData.station}
            isDesktopView={props.isDesktopView}
            setIsBidGroupTabChanged={setIsBidGroupTabChanged}
          />
        ) : isAwardedNameSelected ? (
          <ManageIAMDATBidChoices
            selectedBidGroupEmployeeData={selectedBidGroupEmployeeData}
            referenceNumber={getReferenceNumberFromHref()}
            featureConfig={props.featureConfig}
            setIsManageIAMDATBidChoicesSelected={setIsManageIAMDATBidChoicesSelected}
            handleManageIAMDATBidBackClick={handleManageIAMDATBidBackClick}
            station={props.bidData.station}
            isDesktopView={props.isDesktopView}
            isAwardedEmpReadOnlyView={true}
            selectedAwardedBid={selectedAwardedBid}
          />
        ) : (
          <>
            <StarsBreadcrumbs
              breadcrumbsList={breadcrumbsList}
              breadcrumbsLinks={breadcrumbsLinks}
              isConfirmationRequired={isFormChanged}
              cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
            />
            {/* Title & Cancel/Submit Buttons */}
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{ display: "flex", justifyContent: "start", paddingTop: "10px", paddingLeft: "2rem" }}
              >
                <Typography variant="h5" component="span">
                  Manage DAT Bid
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "2rem" }}>
                <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                  <Button
                    sx={{ width: "100px" }}
                    variant="outlined"
                    color="primary"
                    onClick={handleClickCancelDialogOpen}
                  >
                    Cancel
                  </Button>
                </Box>

                <Box sx={{ paddingLeft: "10px", paddingTop: "10px" }}>
                  <Button
                    sx={{ width: "100px" }}
                    variant="contained"
                    color="primary"
                    onClick={handleClickSubmitDialogOpen}
                    disabled={
                      isDeletedBid(props.bidData.status) ||
                      isAwardedBid(props.bidData.status) ||
                      isBidAllotmentsLoading ||
                      isBidGroupLoading ||
                      props.loadErrors
                    }
                  >
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: "left", marginLeft: "32px" }}>
              <Typography variant="body2" component="span" sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" component="span" sx={{ display: "inline-block" }}>
                  Status:
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: "bold", display: "inline-block", marginLeft: "5px" }}
                >
                  {props.bidData.status}
                </Typography>
                <Box sx={{ display: "inline-block", marginLeft: "5px" }}>
                  <BidScheduledStatusChip scheduledStatus={props.bidData.scheduledStatus} />
                </Box>
                {props.bidData.status !== BID_STATUS.DELETED &&
                  !(
                    props.bidData.status === BID_STATUS.PENDING &&
                    props.bidData.scheduledStatus === BID_SCHEDULED_STATUS.DELAYED
                  ) &&
                  windowDates[0] !== null &&
                  windowDates[1] !== null &&
                  windowDateHr &&
                  windowDateMin && (
                    <>
                      <Typography variant="body2" component="span" sx={{ marginLeft: "5px", marginRight: "5px" }}>
                        |
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ color: "primary.swaRed", fontWeight: "bold", display: "inline-block" }}
                      >
                        <BidTimer windowDates={windowDates} station={props.bidData.station} />
                      </Typography>
                    </>
                  )}
              </Typography>
            </Box>

            {/* The TABS! */}
            <Box sx={{ width: "100%", margin: "auto", paddingLeft: "2rem", paddingRight: "2rem", marginTop: "1rem" }}>
              <Tabs
                value={props.selectedTab}
                onChange={props.handleChangeTab}
                sx={{ width: "inherit", margin: "auto", bgcolor: "secondary.swaGray1" }}
                variant="fullWidth"
              >
                <Tab
                  sx={{ fontWeight: "bold", height: "70px" }}
                  label={MANAGE_TIMEOFF_BID_TABS.SET_UP_THE_TIMEOFF_BID}
                  icon={
                    !isSetUpIAMDATBidTabInputValid() ? <ErrorOutlineIcon sx={{ color: "primary.swaRed" }} /> : undefined
                  }
                  iconPosition="start"
                ></Tab>
                <Tab sx={{ fontWeight: "bold" }} label={MANAGE_TIMEOFF_BID_TABS.MANAGE_BID_GROUP}></Tab>
                <Tab
                  sx={{ fontWeight: "bold" }}
                  label={MANAGE_TIMEOFF_BID_TABS.MANAGE_ALLOTMENTS}
                  icon={!isAllotmentTabValid() ? <ErrorOutlineIcon sx={{ color: "primary.swaRed" }} /> : undefined}
                  iconPosition="start"
                ></Tab>
                {isAwardTabAvailable() && <Tab sx={{ fontWeight: "bold" }} label={MANAGE_TIMEOFF_BID_TABS.AWARD}></Tab>}
              </Tabs>

              {props.selectedTab === 0 && (
                <div>
                  <SetUpIAMDATBid
                    bid={props.bidData}
                    schedMonth={schedMonth}
                    setSchedMonth={handleEffectiveMonthChange}
                    schedYear={schedYear}
                    setSchedYear={handleEffectiveYearChange}
                    effectiveDatesErrors={effectiveDatesErrors}
                    windowDates={windowDates}
                    setWindowDates={handleWindowDateChange}
                    windowDatesErrors={windowDatesErrors}
                    windowDateHr={windowDateHr}
                    setWindowDateHr={handleBidWindowHourChange}
                    windowDateMin={windowDateMin}
                    setWindowDateMin={handleBidWindowMinuteChange}
                    windowDateTimeErrors={windowDateTimeErrors}
                    bidName={bidName}
                    bidNameErrors={bidNameErrors}
                    setBidNameChange={handleBidNameChange}
                  />
                </div>
              )}

              {props.selectedTab === 1 && (
                <div>
                  <CreateIAMDATBidGroup
                    bid={props.bidData}
                    bidGroup={bidGroup}
                    isBidGroupLoading={isBidGroupLoading}
                    setBidGroup={handleBidGroupChange}
                    schedMonth={schedMonth}
                    schedYear={schedYear}
                    windowDates={windowDates}
                    handleEmployeeChoicesAsAdmin={handleEmployeeChoicesAsAdmin}
                    initialBidGroup={initialBidGroup}
                    userSession={userSession}
                    setIsBidGroupManuallyLoaded={setIsBidGroupManuallyLoaded}
                    setIsSchedMonthOrYearChanged={setIsSchedMonthOrYearChanged}
                    setCalcDailyAllotments={setCalcDailyAllotments}
                    setAllotmentDayState={handleAllotmentDayStateChange}
                    bidName={bidName}
                  />
                </div>
              )}
              {props.selectedTab === 2 && (
                <div>
                  <ManageIAMDATAllotments
                    bid={props.bidData}
                    bidGroup={bidGroup}
                    schedMonth={schedMonth}
                    schedYear={schedYear}
                    isBidAllotmentsLoading={isBidAllotmentsLoading}
                    isBidGroupLoading={isBidGroupLoading}
                    calcDailyAllotments={calcDailyAllotments}
                    setCalcDailyAllotments={setCalcDailyAllotments}
                    allotmentDayState={allotmentDayState}
                    setAllotmentDayState={handleAllotmentDayStateChange}
                    isSchedMonthOrYearChanged={isSchedMonthOrYearChanged}
                    setIsSchedMonthOrYearChanged={setIsSchedMonthOrYearChanged}
                    bidName={bidName}
                  />
                </div>
              )}
              {isAwardTabAvailable() && props.selectedTab === 3 && (
                <div>
                  <IAMDATBidAward
                    bid={props.bidData}
                    bidGroup={bidGroup}
                    isBidGroupLoading={isBidGroupLoading}
                    isBidAllotmentsLoading={isBidAllotmentsLoading}
                    isBidGroupTabChanged={isBidGroupTabChanged}
                    isBidAllotmentsTabChanged={isBidAllotmentsTabChanged}
                    schedMonth={schedMonth}
                    schedYear={schedYear}
                    allotmentDayState={allotmentDayState}
                    calcDailyAllotments={calcDailyAllotments}
                    bidName={bidName}
                    setAllotmentDayState={setAllotmentDayState}
                    setInitialAllotmentDayState={setInitialAllotmentDayState}
                    setInitialBidGroup={setInitialBidGroup}
                    setBid={props.setBidData}
                    setBidGroup={setBidGroup}
                    iamDatAwardRows={generateIAMDATDayStateRows(initialAllotmentDayState, initialBidGroup)}
                    unAwardedList={getUnAwardedList(bidGroup)}
                    isFormChanged={isFormChanged}
                    handleAwardedNameClick={handleAwardedNameClick}
                    bidAdminPath={getBidAdminPath()}
                    loadBidData={props.reloadBidData}
                    currentRequestId={props.currentRequestId}
                  />
                </div>
              )}
            </Box>

            <ConfirmationDialog
              isDialogOpen={isExitDialogOpen}
              title={CONFIRM_TITLE}
              message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)}
              handleNoButtonClicked={handleClickCancelDialogClose}
              redirectTo={getBidAdminPath()}
            />
            {isSaveDialogOpen && renderSaveIAMDATBidDialog()}
            <SuccessDialog
              isSuccessDialogOpen={isSuccessDialogOpen}
              title={"Bid Confirmation"}
              subTitle={REFERENCE_NUMBER_SUB_TITLE(props.bidData.referenceNumber)}
              message={"The bid has been saved!"}
              hasError={saveErrors !== ""}
              error={saveErrors}
              handleOKClick={() => {
                setSaveErrors("");
                setIsSuccessDialogOpen(false);
                props.reloadBidData();
              }}
            />
          </>
        )}
      </>
    );
  };

  const renderMobileManageIAMDATBidPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  return (
    <div className="App-contents enable-scroll" data-testid="manage-iam-dat-bid-page">
      {props.isDesktopView ? renderDesktopManageIAMDATBidPage() : renderMobileManageIAMDATBidPage()}
    </div>
  );
};

ManageIAMDATBidPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  bidData: PropTypes.object.isRequired,
  selectedTab: PropTypes.number.isRequired,
  handleChangeTab: PropTypes.func.isRequired,
  setLoadErrors: PropTypes.func.isRequired,
  currentRequestId: PropTypes.object.isRequired,
  loadErrors: PropTypes.bool,
  setBidData: PropTypes.func.isRequired,
  reloadBidData: PropTypes.func.isRequired,
};

export default ManageIAMDATBidPage;
